import SoundManager from "../../../../../lib/html5-software-library/src/ranj/audio/SoundManager";
import Slice9Button from "../../../../../lib/html5-software-library/src/ranj/components/scale9/Slice9Button";
import Slice9Panel from "../../../../../lib/html5-software-library/src/ranj/components/scale9/Slice9Panel";
import Content from "../../../../../lib/html5-software-library/src/ranj/eng/content/Content";
import ContentTypes from "../../../../../lib/html5-software-library/src/ranj/eng/content/ContentTypes";
import ImageContent from "../../../../../lib/html5-software-library/src/ranj/eng/content/ImageContent";
import TextContent from "../../../../../lib/html5-software-library/src/ranj/eng/content/TextContent";
import Anchor from "../../../../../lib/html5-software-library/src/ranj/layout/Anchor";
import SoundType from "../../../core/sound/util/SoundType";
import AbstractMediator from "../../../core/ui/AbstractMediator";
import NGTDialogPlayer from "../../../ngt/dialog/NGTDialogPlayer";
import NGTDialogPlayerEvent from "../../../ngt/dialog/NGTDialogPlayerEvent";
import DebugModel from "../../debug/model/DebugModel";
import DialogUtil from "../../util/DialogUtil";
import DisplayObjectUtil from "../../util/DisplayObjectUtil";
import MovieClipUtil from "../../util/MovieClipUtil";
import Slice9ButtonUtil from "../../util/Slice9ButtonUtil";

export default class NonInteractiveConversationScreenMediator extends AbstractMediator {
    /**
     * @constructor
     * @param {createjs.MovieClip} target 
     * @param {soma.EventDispatcher} dispatcher 
     * @param {LayoutManager} layoutManager 
     * @param {ClickEaterFactory} clickEaterFactory 
     * @param {AssetLibrary} assetLibrary
     * @param {PlayerModel} playerModel
     * @param {RevisableLanguagePack} languagePack 
     * @param {SoundManager} soundManager 
     */
    constructor(target, dispatcher, layoutManager, clickEaterFactory, assetLibrary, playerModel, languagePack, soundManager) {
        super(target, dispatcher, layoutManager, clickEaterFactory);

        this._assetLibrary = assetLibrary;
        this._playerModel = playerModel;
        this._languagePack = languagePack;
        this._soundManager = soundManager;

        /** @type {NGTDialogPlayer} */
        this._dialogPlayer = this.getCustomData().dialogPlayer;

        this._currentBackgroundImageId = null;
    }

    initialize() {
        super.initialize();

        this.preventClickThrough();

        this._slicedTextPanel = new Slice9Panel(this._target.textPanel, MovieClipUtil.getFirstBitmapChild(this._target.textPanel), {
            mode: Slice9Panel.VERTICAL,
            pixelPerfect: true,
            overlap: false,
        });
        this._target.textPanel.initialY = this._target.textPanel.y;

        this._hideTextPanelInstant();
        this._clearBackgroundImage(false);

        Slice9ButtonUtil.persistInitialState(this._target.backButton);
        this._backButton = this.createSlice9Button(this._target.backButton, this._handleBackButton.bind(this), this._languagePack.getText('nonInteractiveConversationScreen.backButtonLabel'), SoundType.BUTTON_CLICK, Slice9Button.HORIZONTAL);
        this._backButton.setVisible(false);
        Slice9ButtonUtil.align(this._backButton, Anchor.R);

        Slice9ButtonUtil.persistInitialState(this._target.continueButton);
        this._continueButton = this.createSlice9Button(this._target.continueButton, this._handleContinueButton.bind(this), this._languagePack.getText('nonInteractiveConversationScreen.continueButtonLabel'), SoundType.BUTTON_CLICK, Slice9Button.HORIZONTAL);
        this._continueButton.setVisible(false);

        this.registerEventListener(NGTDialogPlayerEvent.DIALOG_CONTENT, this._handleDialogContent.bind(this));
        this.registerEventListener(NGTDialogPlayerEvent.COMMAND_SEQUENCE_END, this._handleCommandSequenceEnd.bind(this));

        this._dialogPlayer.viewContent();
    }

    _handleDialogContent(evt) {
        /** @type {Content} */
        const content = evt.params.data.data.content;
        switch (content.getType()) {
            case ContentTypes.TYPE_TEXT:
                this._handleTextContent(content);
                break;
            case ContentTypes.TYPE_IMAGE:
                this._handleImageContent(content);
                break;
        }
    }

    _handleCommandSequenceEnd(evt) {
        const currentOptions = this._dialogPlayer.getCurrentOptions();
        if (currentOptions.length === 0) {
            return;
        }

        const firstOption = currentOptions[0];

        /** @type {TextContent} */
        const optionContent = DialogUtil.extractContentFromOption(firstOption);
        this._continueButton.setLabel(optionContent.getText());
        this._continueButton.setVisible(true);
        Slice9ButtonUtil.align(this._continueButton, Anchor.L);

        const canGoBack = this._dialogPlayer.hasPreviousCommandSequence();
        this._backButton.setVisible(canGoBack);
    }

    /**
     * @param {TextContent} textContent 
     */
    async _handleTextContent(textContent) {
        const playerName = this._playerModel.getTrimmedPlayerName();
        const text = textContent.getText().replace(/{{playerName}}/g, playerName)?.trim();
        await this._showText(text);

        this._dialogPlayer.contentCompleted(textContent);
    }

    /**
     * @param {ImageContent} imageContent 
     */
    async _handleImageContent(imageContent) {
        const imageId = imageContent.getImage();
        await this._setBackgroundImage(imageId);

        this._dialogPlayer.contentCompleted(imageContent);
    }

    async _handleBackButton() {
        this._hideNavigationButtons();
        await this._hideTextPanel();

        this._dialogPlayer.gotoPreviousCommandSequence();
    }

    async _handleContinueButton() {
        this._hideNavigationButtons();
        await this._hideTextPanel();

        const currentOptions = this._dialogPlayer.getCurrentOptions();
        const firstOption = currentOptions[0];
        this._dialogPlayer.optionChosen(firstOption);
    }

    async _clearBackgroundImage(animated = true) {
        this._target.bgImageContainer.gotoAndStop(0);
        if (this._target.bgImageContainer.children.length === 0) {
            return;
        }

        if (!animated) {
            this._target.bgImageContainer.removeAllChildren();
            return;
        }

        const duration = DebugModel.fasterAnimations ? 150 : 450;
        const tweenOutPromises = this._target.bgImageContainer.children.map((child) => {
            return createjs.Tween.get(child, { override: true })
                .to({ alpha: 0 }, duration, createjs.Ease.sineOut)
                .toPromise();
        });
        await Promise.all(tweenOutPromises);

        this._target.bgImageContainer.removeAllChildren();
    }

    async _hideNavigationButtons() {
        this._backButton.setVisible(false);
        this._continueButton.setVisible(false);
    }

    async _setBackgroundImage(imageId) {
        if (this._currentBackgroundImageId === imageId) {
            // The correct background image is already shown.
            return;
        }

        this._currentBackgroundImageId = imageId;

        await this._clearBackgroundImage(true);

        // Get asset, convert to bitmap, set anchor to center, add as child
        const image = this._assetLibrary.getAsset(imageId);

        if (!image) {
            console.warn(`Image with id '${imageId}' does not exist!`);
            return;
        }

        const bitmap = new createjs.Bitmap(image);
        DisplayObjectUtil.anchorDisplayObject(bitmap, Anchor.C);
        bitmap.alpha = 0;
        this._target.bgImageContainer.addChild(bitmap);

        const duration = DebugModel.fasterAnimations ? 150 : 450;
        await createjs.Tween.get(bitmap, { override: true })
            .to({ alpha: 1 }, duration, createjs.Ease.sineIn)
            .toPromise();
    }

    async _hideTextPanelInstant() {
        this._target.textPanel.alpha = 0;
    }

    async _hideTextPanel() {
        const duration = DebugModel.fasterAnimations ? 150 : 450;
        await createjs.Tween.get(this._target.textPanel)
            .to({ alpha: 0 }, duration, createjs.Ease.sineInOut)
            .toPromise();
    }

    async _showTextPanel() {
        const duration = DebugModel.fasterAnimations ? 150 : 450;
        await createjs.Tween.get(this._target.textPanel)
            .to({ alpha: 1 }, duration, createjs.Ease.sineInOut)
            .toPromise();
    }

    async _showText(text) {
        this._target.textPanel.narrativeTextField.text = text;
        this._slicedTextPanel.update();

        const deltaHeight = this._slicedTextPanel.getDeltaHeight();
        this._target.textPanel.y = this._target.textPanel.initialY - deltaHeight;

        await this._showTextPanel();
    }
}